// src/components/MeetingsTab.jsx
// Displays meeting history for an account.
// Victor automatically reviews every meeting — no manual trigger needed.
// The tab shows Victor's strategic analysis alongside Jarvis's extraction.

import React, { useState, useEffect, useCallback } from 'react';
import './MeetingsTab.css';
import { apiFetch } from '../lib/api';

function MeetingsTab({ accountId, accountName, onNavigate }) {
  const [meetings, setMeetings] = useState([]);
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState(null);
  const [selected, setSelected] = useState(null);
  const [activePanel, setActivePanel] = useState('victor'); // 'victor' | 'jarvis' | 'transcript'

  const fetchMeetings = useCallback(async () => {
    try {
      setLoading(true);
      const res  = await apiFetch(`/api/meetings/by-account?accountId=${accountId}`);
      const data = await res.json();
      if (data.success) {
        setMeetings(data.meetings);
      } else {
        setError(data.error || 'Failed to load meetings');
      }
    } catch {
      setError('Failed to fetch meetings');
    } finally {
      setLoading(false);
    }
  }, [accountId]);

  useEffect(() => { fetchMeetings(); }, [fetchMeetings]);

  const formatDate = (d) => {
    if (!d) return '—';
    return new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const formatDuration = (mins) => {
    if (!mins) return null;
    return mins < 60 ? `${mins}m` : `${Math.floor(mins/60)}h ${mins%60 ? `${mins%60}m` : ''}`.trim();
  };

  const handleSelectMeeting = (meeting) => {
    if (selected?.id === meeting.id) {
      setSelected(null);
    } else {
      setSelected(meeting);
      setActivePanel('victor');
    }
  };

  if (loading) return <div className="mt-loading">Loading meetings…</div>;
  if (error)   return <div className="mt-error">⚠️ {error}</div>;

  return (
    <div className="meetings-tab">
      <div className="mt-header">
        <div className="mt-header-left">
          <h3 className="mt-title">Meeting History</h3>
          <span className="mt-count">{meetings.length}</span>
        </div>
        <button
          className="mt-start-btn"
          onClick={() => onNavigate && onNavigate('meeting-from-account', accountId)}
        >
          🎙️ Start Meeting
        </button>
      </div>

      {meetings.length === 0 ? (
        <div className="mt-empty">
          <div className="mt-empty-icon">🎙️</div>
          <p className="mt-empty-title">No meetings recorded yet</p>
          <p className="mt-empty-sub">Record a meeting to get automatic Victor analysis, tasks, and research requests.</p>
          <button className="mt-empty-btn" onClick={() => onNavigate && onNavigate('meeting-from-account', accountId)}>
            Start First Meeting
          </button>
        </div>
      ) : (
        <div className="mt-layout">
          {/* Left: Meeting list */}
          <div className="mt-list">
            {meetings.map(m => (
              <div
                key={m.id}
                className={`mt-card ${selected?.id === m.id ? 'active' : ''}`}
                onClick={() => handleSelectMeeting(m)}
              >
                <div className="mt-card-top">
                  <div className="mt-card-title">{m.title}</div>
                  <div className="mt-card-date">{formatDate(m.date)}</div>
                </div>
                <div className="mt-card-meta">
                  {formatDuration(m.durationMinutes) && (
                    <span className="mt-meta-pill">⏱ {formatDuration(m.durationMinutes)}</span>
                  )}
                  {m.tasksCreated > 0 && (
                    <span className="mt-meta-pill">✅ {m.tasksCreated} task{m.tasksCreated !== 1 ? 's' : ''}</span>
                  )}
                  {(m.victorTasksCreated || 0) > 0 && (
                    <span className="mt-meta-pill mt-victor-task-pill">🧠 {m.victorTasksCreated} Victor</span>
                  )}
                  {m.signalsCreated > 0 && (
                    <span className="mt-meta-pill">⚡ {m.signalsCreated} signal{m.signalsCreated !== 1 ? 's' : ''}</span>
                  )}
                  {m.opportunityDetected && (
                    <span className="mt-meta-pill mt-opp-pill">🎯 Opportunity</span>
                  )}
                  <span className="mt-meta-pill mt-victor-pill">🧠 Victor Reviewed</span>
                </div>
                {m.victorStrategicSummary ? (
                  <div className="mt-card-summary victor-summary">{m.victorStrategicSummary}</div>
                ) : m.summary ? (
                  <div className="mt-card-summary">{m.summary}</div>
                ) : null}
              </div>
            ))}
          </div>

          {/* Right: Analysis panel */}
          {selected && (
            <div className="mt-detail">
              <div className="mt-detail-header">
                <div className="mt-detail-header-left">
                  <div className="mt-detail-title">{selected.title}</div>
                  <div className="mt-detail-date">{formatDate(selected.date)}</div>
                </div>
                <button className="mt-detail-close" onClick={() => setSelected(null)}>×</button>
              </div>

              {/* Panel switcher */}
              <div className="mt-panel-tabs">
                <button
                  className={`mt-panel-tab ${activePanel === 'victor' ? 'active' : ''}`}
                  onClick={() => setActivePanel('victor')}
                >
                  🧠 Victor's Analysis
                </button>
                <button
                  className={`mt-panel-tab ${activePanel === 'jarvis' ? 'active' : ''}`}
                  onClick={() => setActivePanel('jarvis')}
                >
                  📊 Intelligence
                </button>
                <button
                  className={`mt-panel-tab ${activePanel === 'transcript' ? 'active' : ''}`}
                  onClick={() => setActivePanel('transcript')}
                >
                  📝 Transcript
                </button>
              </div>

              <div className="mt-detail-body">
                {/* ── Victor's Analysis Panel ── */}
                {activePanel === 'victor' && (
                  <div className="mt-victor-panel">
                    <div className="mt-victor-badge">
                      <span className="mt-victor-icon">🧠</span>
                      <span>Victor reviewed this meeting automatically</span>
                    </div>

                    {selected.victorStrategicSummary && (
                      <div className="mt-section">
                        <div className="mt-section-label">Strategic Summary</div>
                        <div className="mt-section-content victor-text">{selected.victorStrategicSummary}</div>
                      </div>
                    )}

                    {selected.victorOpportunityRead && (
                      <div className="mt-section mt-opp-section">
                        <div className="mt-section-label">🎯 Opportunity Read</div>
                        <div className="mt-section-content">{selected.victorOpportunityRead}</div>
                      </div>
                    )}

                    {selected.victorNext30Days && (
                      <div className="mt-section mt-focus-section">
                        <div className="mt-section-label">📅 Next 30 Days</div>
                        <div className="mt-section-content">{selected.victorNext30Days}</div>
                      </div>
                    )}

                    {selected.victorResearchRequests && (
                      <div className="mt-section">
                        <div className="mt-section-label">🔍 Research Requests Queued</div>
                        <div className="mt-section-content research-text">
                          {selected.victorResearchRequests.split('\n').filter(Boolean).map((line, i) => (
                            <div key={i} className="mt-research-line">{line}</div>
                          ))}
                        </div>
                      </div>
                    )}

                    {selected.outreachBriefForCyrano && selected.outreachBriefForCyrano !== 'No outreach needed at this time' && (
                      <div className="mt-section mt-outreach-section">
                        <div className="mt-section-label">✉️ Outreach Brief for Cyrano</div>
                        <div className="mt-section-content">
                          {selected.outreachBriefForCyrano.split('\n').map((line, i) => (
                            <div key={i}>{line}</div>
                          ))}
                        </div>
                      </div>
                    )}

                    {selected.victorAnalysis && (
                      <div className="mt-section">
                        <div className="mt-section-label">Additional Notes</div>
                        <div className="mt-section-content muted-text">{selected.victorAnalysis}</div>
                      </div>
                    )}

                    {/* Stats row */}
                    <div className="mt-stats-row">
                      <div className="mt-stat">
                        <div className="mt-stat-val">{selected.tasksCreated || 0}</div>
                        <div className="mt-stat-lbl">Jarvis Tasks</div>
                      </div>
                      <div className="mt-stat">
                        <div className="mt-stat-val">{selected.victorTasksCreated || 0}</div>
                        <div className="mt-stat-lbl">Victor Tasks</div>
                      </div>
                      <div className="mt-stat">
                        <div className="mt-stat-val">{selected.signalsCreated || 0}</div>
                        <div className="mt-stat-lbl">Signals</div>
                      </div>
                      <div className="mt-stat">
                        <div className="mt-stat-val">{selected.opportunityDetected ? '✓' : '—'}</div>
                        <div className="mt-stat-lbl">Opportunity</div>
                      </div>
                    </div>
                  </div>
                )}

                {/* ── Jarvis Intelligence Panel ── */}
                {activePanel === 'jarvis' && (
                  <div>
                    {selected.summary && (
                      <div className="mt-section">
                        <div className="mt-section-label">Meeting Summary</div>
                        <div className="mt-section-content">{selected.summary}</div>
                      </div>
                    )}

                    {selected.keyInsights && (
                      <div className="mt-section">
                        <div className="mt-section-label">Key Insights</div>
                        <div className="mt-section-content">
                          {selected.keyInsights.split('\n').filter(Boolean).map((line, i) => (
                            <div key={i} className="mt-insight-line">{line}</div>
                          ))}
                        </div>
                      </div>
                    )}

                    {selected.opportunityDetected && selected.opportunityHypothesis && (
                      <div className="mt-section mt-opp-section">
                        <div className="mt-section-label">🎯 Opportunity Detected</div>
                        <div className="mt-section-content">{selected.opportunityHypothesis}</div>
                      </div>
                    )}

                    {selected.contactsMentioned && (
                      <div className="mt-section">
                        <div className="mt-section-label">Contacts Mentioned</div>
                        <div className="mt-section-content">{selected.contactsMentioned}</div>
                      </div>
                    )}

                    {selected.accountUpdates && (
                      <div className="mt-section">
                        <div className="mt-section-label">Account Updates Applied</div>
                        <div className="mt-section-content">{selected.accountUpdates}</div>
                      </div>
                    )}
                  </div>
                )}

                {/* ── Transcript Panel ── */}
                {activePanel === 'transcript' && (
                  <div className="mt-section">
                    <div className="mt-section-label">Full Transcript</div>
                    {selected.fullTranscript ? (
                      <div className="mt-transcript">{selected.fullTranscript}</div>
                    ) : (
                      <div className="mt-no-transcript">Transcript not available for this meeting</div>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default MeetingsTab;
